import { Menu } from 'antd5';
import styled, { type DefaultTheme, css } from 'styled-components';

import { Sider } from 'components/ui';
import { BRAND_ENERGY_GRADIENT } from 'utils/color/horizonTheme';
import { FlexContainer } from 'utils/layouts/containers';

const SIDEBAR_TRIGGER_HEIGHT = '48px';

export const Logo = styled(FlexContainer)`
  background: transparent;
  text-align: center;
  overflow: hidden;
  height: 65px;
  justify-content: center;
  border-bottom: 1px solid ${({ theme }) => theme.sidebarBorder};
`;

export const Image = styled.img<{ $collapsed?: boolean; $invertForNavyRail?: boolean }>`
  width: ${({ $collapsed }) => ($collapsed ? '48px' : '100%')};
  max-height: ${({ $collapsed }) => ($collapsed ? '48px' : '52px')};
  object-fit: contain;
  padding: ${({ $collapsed }) => ($collapsed ? '0.5em' : '0.5rem 0.75rem')};
  filter: ${({ $invertForNavyRail }) => ($invertForNavyRail ? 'brightness(0) invert(1)' : 'none')};
`;

// !important for overriding antd
// Trevor: Not sure where the "margin-right: 10px" is coming from on the icon property
// for the Menu, but it causes a larger space between the icon and title than what we want.
// When inspecting the antd menu on their examples on the antd site. It does not have
// the "margin-right: 10px" property.
export const StyledMenu = styled(Menu)`
  width: 100%;
  .ant-menu-item {
    font-size: 16px !important;
  }
  .ant-menu-item .ant-menu-item-icon {
    margin-right: 0px;
  }

  .ant-menu-item-disabled,
  .ant-menu-item-disabled > span,
  .ant-menu-item-disabled > span > a {
    color: ${({ theme }) => theme.sidebarTextMuted} !important;
  }

  .ant-menu-item-disabled {
    display: flex;

    svg {
      stroke: ${({ theme }) => theme.sidebarTextMuted} !important;
      color: ${({ theme }) => theme.sidebarTextMuted} !important;
    }

    .ant-menu-title-content {
      display: inline-flex;
      flex-grow: 1;

      span {
        width: 100%;
      }
    }
  }
`;

export const SidebarFooter = styled.div<{ $collapsed?: boolean }>`
  padding: ${({ $collapsed }) => ($collapsed ? '0.75rem 0.5rem 0.25rem' : '0.75rem 1rem 0.25rem')};
  border-top: 1px solid ${({ theme }) => theme.sidebarBorder};
  overflow: hidden;
`;

export const SidebarMeta = styled.div`
  position: absolute;
  right: 0;
  bottom: ${SIDEBAR_TRIGGER_HEIGHT};
  left: 0;
  display: flex;
  flex-direction: column;
`;

export const VersionLabel = styled.span<{ $collapsed?: boolean }>`
  display: block;
  padding: ${({ $collapsed }) => ($collapsed ? '0 0.25rem 0.5rem' : '0 1rem 0.5rem')};
  /* Hide the version so it blends into the navy rail but stays selectable:
   * transparent text is invisible to most users yet readable when highlighted
   * (revealed via ::selection below, with cursor/user-select). */
  ${({ theme }) => css`
    color: transparent;
    ::selection {
      color: ${theme.sidebarText};
      background: ${theme.sidebarBg};
    }
  `}
  font-size: 0.75rem;
  line-height: 1.3;
  text-align: center;
  user-select: text;
  cursor: text;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: clip;
`;

export const PrivacyPolicyLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  white-space: nowrap;
  color: ${({ theme }) => theme.sidebarText};

  &:hover {
    color: ${({ theme }) => theme.teal};
  }
`;

/**
 * antd's Sider component has a 'theme' prop, so we need to rename the
 * styled-components theme prop here.
 *
 * Reskin rail: solid navy (`sidebarBg`) with the SkySpecs energy accent strip
 * along the top edge.
 */
export const StyledSider = styled(Sider)<{ $styledComponentsTheme: DefaultTheme }>`
  background: ${({ $styledComponentsTheme }) => $styledComponentsTheme.sidebarBg} !important;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${BRAND_ENERGY_GRADIENT};
    z-index: 2;
  }
  border-right: 1px solid ${({ $styledComponentsTheme }) => $styledComponentsTheme.sidebarBorder};
  display: flex;
  flex-direction: column;
  position: relative;

  .ant-layout-sider-children {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 100%;
  }

  .ant-menu,
  .ant-menu-dark {
    background: transparent !important;

    .ant-menu-item {
      color: ${({ $styledComponentsTheme }) => $styledComponentsTheme.sidebarText} !important;

      > span > a {
        color: ${({ $styledComponentsTheme }) => $styledComponentsTheme.sidebarText} !important;
      }

      svg {
        color: ${({ $styledComponentsTheme }) => $styledComponentsTheme.teal} !important;
      }

      &.ant-menu-item-selected {
        color: ${({ $styledComponentsTheme }) =>
          $styledComponentsTheme.mapControlsTextColor} !important;
        /* Semi-transparent tint — only on the row so nested link/title don't stack */
        background-color: ${({ $styledComponentsTheme }) =>
          $styledComponentsTheme.sidebarActiveBg} !important;
        box-shadow: inset 3px 0 0 ${({ $styledComponentsTheme }) => $styledComponentsTheme.teal};
      }

      &.ant-menu-item-selected > span > a,
      &.ant-menu-item-selected .ant-menu-title-content {
        color: ${({ $styledComponentsTheme }) =>
          $styledComponentsTheme.mapControlsTextColor} !important;
        background-color: transparent !important;
      }

      &.ant-menu-item-selected svg {
        color: ${({ $styledComponentsTheme }) =>
          $styledComponentsTheme.mapControlsTextColor} !important;
      }

      &:hover {
        background-color: ${({ $styledComponentsTheme }) =>
          $styledComponentsTheme.sidebarActiveBg} !important;
      }
    }
  }

  .ant-layout-sider-trigger {
    background: ${({ $styledComponentsTheme }) => $styledComponentsTheme.sidebarBorder} !important;

    svg {
      color: ${({ $styledComponentsTheme }) => $styledComponentsTheme.sidebarText} !important;
    }
  }
`;
